import type { Task, TaskStatus } from '../types';

const taskStatuses = new Set(['todo', 'progress', 'review', 'done', 'blocked', 'off-track']);
export interface UnsupportedTaskSummary {
  key: string;
  id: string;
  projectId: string | null;
  title: string;
  statusLabel: string;
  issueLabel?: string;
  projectLabel?: string;
}
export interface ProjectTaskSnapshot {
  tasks: Task[];
  unsupportedTasks: UnsupportedTaskSummary[];
}

function validIdentityPart(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0 && value.length <= 255
    && value.trim() === value && !/[\u0000-\u001f\u007f/\\?#]/.test(value);
}

function identityIssue(task: Record<string, unknown>): string | undefined {
  if (!validIdentityPart(task.id)) return 'Invalid task ID';
  if (task.projectId != null && !validIdentityPart(task.projectId)) return 'Invalid project ID';
  if (task._identityIssue !== undefined) return 'Duplicate task ID';
  return undefined;
}

/** Keep ambiguous identities and legacy values out of every editable Task array. */
export function partitionProjectTasks(rows: unknown[]): ProjectTaskSnapshot {
  const result: ProjectTaskSnapshot = { tasks: [], unsupportedTasks: [] };
  const counts = new Map<string, number>();
  for (const row of rows) {
    const id = (row as Record<string, unknown> | null)?.id;
    if (typeof id === 'string') counts.set(id, (counts.get(id) || 0) + 1);
  }
  for (const [index, row] of rows.entries()) {
    const task = row && typeof row === 'object' && !Array.isArray(row) ? row as Record<string, unknown> : {};
    const issue = typeof task.id === 'string' && (counts.get(task.id) || 0) > 1 ? 'Duplicate task ID' : identityIssue(task);
    if (!issue && typeof task.status === 'string' && taskStatuses.has(task.status)) {
      result.tasks.push(row as Task);
    } else {
      result.unsupportedTasks.push({
        key: `issue-${index}`,
        id: validIdentityPart(task.id) ? task.id : 'Unavailable task ID',
        projectId: validIdentityPart(task.projectId) ? task.projectId : null,
        ...(task.projectId != null && !validIdentityPart(task.projectId) ? { projectLabel: 'Invalid project ID' } : {}),
        ...(issue ? { issueLabel: issue } : {}),
        title: typeof task.title === 'string' && task.title.trim() ? task.title.slice(0, 240) : 'Untitled task',
        statusLabel: task.status == null ? 'Missing status'
          : typeof task.status === 'string' ? (task.status.trim() ? task.status.slice(0, 120) : 'Empty status') : 'Invalid status value',
      });
    }
  }
  return result;
}

export interface TaskBoardPage {
  todo: Task[];
  progress: Task[];
  review: Task[];
  done: Task[];
  blocked: Task[];
  'off-track': Task[];
  unsupported?: unknown[];
  _meta: { capped: boolean; continuationToken?: string | null; pageSize: number };
}

export interface TaskBoardOptions {
  pagination: 'cursor';
  includeDone: true;
  includeUnsupported: true;
  includeIdentityIssues: true;
  pageSize: number;
  continuationToken?: string;
}

/** Publish only a complete snapshot; neither metadata nor partial pages are tasks. */
export async function loadProjectTaskPages(readPage: (options: TaskBoardOptions) => Promise<TaskBoardPage>): Promise<ProjectTaskSnapshot> {
  const rows: unknown[] = [];
  const seen = new Set<string>();
  let continuationToken: string | undefined;
  for (let page = 0; page < 100; page++) {
    const result = await readPage({ pagination: 'cursor', includeDone: true, includeUnsupported: true, includeIdentityIssues: true, pageSize: 200, continuationToken });
    for (const bucket of ['todo', 'progress', 'review', 'done', 'blocked', 'off-track'] as const) {
      if (!Array.isArray(result?.[bucket])) throw new Error('Task page is incomplete.');
      for (const task of result[bucket]) {
        if (!task || task.status !== bucket) {
          throw new Error('Task page contains an invalid task.');
        }
        rows.push(task);
      }
    }
    // Six-bucket older servers reject unsupported rows rather than hiding them.
    if (result.unsupported !== undefined) {
      if (!Array.isArray(result.unsupported)) throw new Error('Task page contains an invalid unsupported bucket.');
      for (const row of result.unsupported) {
        if (row && typeof row === 'object' && !identityIssue(row as Record<string, unknown>) && taskStatuses.has((row as Task).status as TaskStatus)) {
          throw new Error('Task page contains a task in the wrong bucket.');
        }
        rows.push(row);
      }
    }
    const meta = result._meta;
    if (!meta || typeof meta.capped !== 'boolean') throw new Error('Task paging information is missing.');
    const next = meta.continuationToken;
    if (next == null && meta.capped === false) return partitionProjectTasks(rows);
    if (typeof next !== 'string' || !next || seen.has(next)) throw new Error('Task paging could not be completed.');
    seen.add(next);
    continuationToken = next;
  }
  throw new Error('Task paging limit reached.');
}
